import shortId from 'short-uuid';
import moment from 'moment';
import { isEmpty, isNil } from 'lodash';
import { TRIP_TYPE } from '@/constant/constant';
import {
  INITALIZE_TRIP_INQUIRY_FORMIK_FORM,
  INITALIZE_SPECIAL_REQUEST_FORMIK_FORM,
  RESET_TRIP_INQUIRY_FORMIK_FORM,
  UPDATE_TRIP_INQUIRY_FORMIK_FORM_EMAIL,
  UPDATE_TRIP_INQUIRY_FORMIK_FORM_FLIGHT_CREDIT,
  UPDATE_TRIP_INQUIRY_FORMIK_FORM_JET_CARD_SPLIT,
} from '@/constant/redux-const';
import { TRIP_TYPE_KEY } from '@/constant/enum';
import { convertMinutesInHourAndMinutes } from '@/utils/duration';
import { IPassenger } from '@/service/contact';
import { IFuelStop } from '@/pages/trip/manageTrip/createTrip/stepsForm/proposalForm/interface/ILeg';
import { withSavedDefaultDepartures } from '@/pages/trip/common/fuelStops/fuelStopTime';

export interface ILegsFormikValues {
    id: number | null
    key: string;
    originAirportId: number | null;
    originAirportTimezone: string | null;
    destinationAirportId: number | null;
    destinationAirportTimezone: string | null;
    international: boolean;
    peak: boolean;
    departDate: Date;
    departTime: Date | null;
    duration: string;
    numberOfPassengers: number | string;
    passengerIds: Array<number>;
    leadPassengerId?: number;
    legAircraftTypeIds: Array<number>;
    flightCreditApplied?: {
        dollars: number;
        hours: number;
        aircraftTypeId: number;
        dollarsForDisplay?: number;
        hoursForDisplay?: number;
    };
    excludedCreditJetCardIds?: number[];
    numberOfPets: number | string;
    petIds: Array<number>;
    luggage: Array<Luggage>;
    fuelStops?: Array<IFuelStop>;
    eteToArrivalMinutes?: number | null;
    directEteMinutes?: number | null;
}
export interface ISpecialRequestsFormikValues {
    key: string;
    typeId: number | null;
    name: string;
    fee: number | string;
    shortDescription: string;
    legIndex: Array<number>;
    forAllLeg: boolean;
}
export interface IAdditionalNotesFormikValues {
    id: number | null;
    key: string;
    note: string;
    isPinned: boolean;
}
export interface IExtraFormikValues {
    preNotesSelectedList: Array<IAdditionalNotesFormikValues>;
    isRoundTrip: boolean;
}
export interface ITripInquiryFormikValues {
    clientId: number | null;
    clientEmail: string | null;
    brokerIds: Array<number>;
    handledByIds: Array<number>;
    tripTypeId: number;
    ballparkAmount?: number | string;
    jetCardId?: number | null;
    /** Cards behind the primary, in drain order. Never contains jetCardId. */
    additionalJetCardIds: (number | null)[];
    applyFlightCredit?: boolean;
    selectedFlightCreditId?: number | null;
    aircraftTypeIds: Array<number>;
    legs: Array<ILegsFormikValues>;
    specialRequests: Array<ISpecialRequestsFormikValues>;
    additionalNotes: Array<IAdditionalNotesFormikValues>;
    extra: IExtraFormikValues;
}

export interface Luggage {
  luggageTypeId: number,
  quantity: number,
  notes: string
}

let emptyLegsObject: Array<ILegsFormikValues> = [
  {
    id: null,
    key: shortId.generate(),
    originAirportId: null,
    originAirportTimezone: null,
    destinationAirportId: null,
    destinationAirportTimezone: null,
    international: false,
    peak: false,
    departDate: moment().toDate(),
    departTime: null,
    duration: '',
    numberOfPassengers: '',
    passengerIds: [],
    leadPassengerId: undefined,
    legAircraftTypeIds: [],
    flightCreditApplied: undefined,
    numberOfPets: '',
    petIds: [],
    luggage: [],
  },
];
let emptySpecialRequestsObject: Array<ISpecialRequestsFormikValues>= [
  {
    key: shortId.generate(),
    typeId: null,
    name: '',
    fee: '',
    shortDescription: '',
    legIndex: [ 0 ],
    forAllLeg: true,
  },
];
let emptyAdditionalNotesObject: Array<IAdditionalNotesFormikValues> = [
  {
    id: null,
    key: shortId.generate(),
    note: '',
    isPinned: false,
  },
];
let emptyExtraObject: IExtraFormikValues = {
  preNotesSelectedList: [],
  isRoundTrip: false,
};

const initialState: any = {
  /**
   * The trip the form currently holds, so INITALIZE can tell "the broker stepped back
   * onto the same trip" from "a different trip was opened". Not part of the form values:
   * nothing renders it and no payload carries it.
   */
  tripInquiryFormikFormTripId: null,
  tripInquiryFormikForm: {
    clientId: null,
    clientEmail: null,
    brokerIds: [],
    handledByIds: [],
    tripTypeId: TRIP_TYPE[0].id,
    ballparkAmount: '',
    jetCardId: null,
    additionalJetCardIds: [],
    aircraftTypeIds: [],
    legs: emptyLegsObject,
    specialRequests: emptySpecialRequestsObject,
    additionalNotes: emptyAdditionalNotesObject,
    extra: emptyExtraObject,
  },
};

const formikDataReducer = (state = initialState, action: any) => {
  switch (action.type) {
    case INITALIZE_TRIP_INQUIRY_FORMIK_FORM: {
      if (action.payload) {
        const tripOverviewData = action.payload.overview;
        const duplicateTrip = action.payload.duplicateTrip;

        const handledByIds: Array<number> = [];
        const brokerIds: Array<number> = [];
        tripOverviewData?.brokers?.forEach((broker: any) => {
          if (broker.isHandler) {
            handledByIds.push(broker.id);
          } else {
            brokerIds.push(broker.id);
          }
        });

        const aircraftTypeIds = isEmpty(tripOverviewData.airCraftTypes) ? [] : tripOverviewData.airCraftTypes.map((aircraft: any) => aircraft.id);

        const legs = tripOverviewData.legs.map((leg: any) => {
          const { durationHoursString, durationMinutesString } = convertMinutesInHourAndMinutes(leg.duration);
          return {
            id: leg.id,
            key: leg.id,
            originAirportId: leg.originAirport.id,
            originAirportTimezone: leg.originAirport.timezone,
            destinationAirportId: leg.destinationAirport.id,
            destinationAirportTimezone: leg.destinationAirport.timezone,
            international: leg.international ?? false,
            peak: leg.peak ?? false,
            departDate: duplicateTrip ? null : moment(leg.departDate, 'MM-DD-yyyy').toDate(),
            departTime: moment(leg.departTime, 'HH:mm:ss').toDate(),
            duration: `${durationHoursString}:${durationMinutesString}`,
            numberOfPassengers: leg.numberOfPassengers.toString(),
            numberOfPets: leg?.numberOfPets,
            luggage: leg?.luggage,
            // Not coalesced to []: the form would then submit "delete every stop".
            fuelStops: withSavedDefaultDepartures(leg?.fuelStops),
            eteToArrivalMinutes: leg?.eteToArrivalMinutes ?? null,
            directEteMinutes: leg?.directEteMinutes ?? null,
            passengerIds: leg.passengers ? leg.passengers.map((item: IPassenger) => item.id) : [],
            leadPassengerId: leg.leadPassengerId ? leg.leadPassengerId : null,
            legAircraftTypeIds: leg.aircraftTypes ? leg.aircraftTypes.map((item: any) => item.id) : [],
            excludedCreditJetCardIds: leg.excludedCreditJetCardIds ?? [],
          };
        });

        /**
         * The cards behind the primary, in drain order, as the trip read reports them —
         * either as a plain id list or, once a funding block is returned, as its
         * allocations with the primary at position 1 (R3 in
         * docs/split-payment-backend-api-requirements.md).
         */
        const savedAdditionalJetCardIds: number[] | undefined =
          tripOverviewData.additionalJetCardIds
          ?? (tripOverviewData.funding?.allocations
            ? tripOverviewData.funding.allocations
              .slice(1)
              .map((allocation: any) => allocation.jetCardId)
            : undefined);

        /**
         * Stepping back from Proposal remounts the Details step, which re-INITALIZEs it.
         * Dropping the split there charges the wrong cards — quietly, since the selector
         * then shows none either — so the selection the broker already made is kept when
         * the trip has not changed. Keyed on the trip id, never on the form alone: a
         * split belongs to one trip and must not follow the broker into the next one.
         */
        const isSameTrip =
          !duplicateTrip
          && !isNil(tripOverviewData.id)
          && state.tripInquiryFormikFormTripId === tripOverviewData.id;
        const additionalJetCardIds =
          savedAdditionalJetCardIds
          ?? (isSameTrip ? state.tripInquiryFormikForm?.additionalJetCardIds ?? [] : []);

        const legWithCredit = tripOverviewData.legs?.find(
          (leg: any) => leg.flightCreditUsage?.flightCreditId
        );
        // The saved Charter selection is authoritative, including null when it was cleared.
        // Older responses without the trip-level field still use per-leg usage or the prior
        // Charter selection to preserve the selection between steps.
        const backendFlightCreditId =
          tripOverviewData.selectedFlightCreditId ??
          legWithCredit?.flightCreditUsage?.flightCreditId ??
          null;
        const isCharterTrip =
          TRIP_TYPE.find((type) => type.id === tripOverviewData.tripType?.id)?.key ===
          TRIP_TYPE_KEY.CHARTER;
        // A split trip's credit is the primary's entry in the saved split, which the server prices;
        // the legs' own usage rows describe the same credit and must not outrank it.
        const splitFlightCredits: Array<{ jetCardId: number; flightCreditId: number | null }> | null =
          Array.isArray(tripOverviewData.splitFlightCredits) ? tripOverviewData.splitFlightCredits : null;
        const splitPrimaryCreditId = splitFlightCredits
          ? splitFlightCredits.find((entry) => entry.jetCardId === tripOverviewData.jetCardId)?.flightCreditId ?? null
          : null;
        let selectedFlightCreditId: number | null;
        if (splitFlightCredits) {
          selectedFlightCreditId = splitPrimaryCreditId;
        } else if (isCharterTrip && tripOverviewData.selectedFlightCreditId !== undefined) {
          selectedFlightCreditId = tripOverviewData.selectedFlightCreditId;
        } else {
          selectedFlightCreditId =
            backendFlightCreditId ?? (isCharterTrip ? state.tripInquiryFormikForm?.selectedFlightCreditId ?? null : null);
        }
        const applyFlightCredit = selectedFlightCreditId !== null;

        const additionalNotes = isEmpty(tripOverviewData.additionalNotes) ? emptyAdditionalNotesObject : tripOverviewData.additionalNotes.map((note: any) => ({
          ...note,
          clientId: tripOverviewData.client.id,
          key: shortId.generate(),
        }));

        const isRoundTrip = tripOverviewData.legs?.length === 2 &&
            tripOverviewData.legs[0].originAirport.id === tripOverviewData.legs[1].destinationAirport.id &&
            tripOverviewData.legs[0].destinationAirport.id === tripOverviewData.legs[1].originAirport.id;
        return {
          ...state,
          tripInquiryFormikFormTripId: duplicateTrip ? null : tripOverviewData.id ?? null,
          tripInquiryFormikForm: {
            clientId: tripOverviewData.client.id,
            clientEmail: tripOverviewData.client.email,
            brokerIds,
            handledByIds,
            tripTypeId: tripOverviewData.tripType.id,
            ballparkAmount: tripOverviewData.ballparkAmount ? tripOverviewData.ballparkAmount : '',
            jetCardId: tripOverviewData.jetCardId,
            additionalJetCardIds,
            aircraftTypeIds: aircraftTypeIds,
            // A trip created without an itinerary (the Salesforce draft) starts with the same
            // blank leg a new trip does, so the form has a row to fill in.
            legs: isEmpty(legs) ? emptyLegsObject : legs,
            specialRequests: emptySpecialRequestsObject,
            additionalNotes: additionalNotes,
            extra: {
              ...emptyExtraObject,
              isRoundTrip,
            },
            selectedFlightCreditId,
            applyFlightCredit,
          },
        };
      }
      return state;
    }
    case UPDATE_TRIP_INQUIRY_FORMIK_FORM_EMAIL: {
      return {
        ...state,
        tripInquiryFormikForm: {
          ...state.tripInquiryFormikForm,
          clientEmail: action.payload,
        },
      };
    }
    case UPDATE_TRIP_INQUIRY_FORMIK_FORM_FLIGHT_CREDIT: {
      return {
        ...state,
        tripInquiryFormikForm: {
          ...state.tripInquiryFormikForm,
          selectedFlightCreditId: action.payload.selectedFlightCreditId,
          applyFlightCredit: action.payload.applyFlightCredit,
        },
      };
    }
    case UPDATE_TRIP_INQUIRY_FORMIK_FORM_JET_CARD_SPLIT: {
      return {
        ...state,
        tripInquiryFormikFormTripId:
          action.payload.tripId ?? state.tripInquiryFormikFormTripId,
        tripInquiryFormikForm: {
          ...state.tripInquiryFormikForm,
          additionalJetCardIds: action.payload.additionalJetCardIds,
        },
      };
    }
    case INITALIZE_SPECIAL_REQUEST_FORMIK_FORM: {
      if (!isEmpty(action.payload)) {
        const specialRequests = action.payload.map((specialRequest: any) => {
          return {
            key: shortId.generate(),
            typeId: specialRequest.typeId,
            name: specialRequest.name,
            fee: specialRequest.fee.toString(),
            shortDescription: specialRequest.shortDescription,
            legIndex: specialRequest.legIndex,
            forAllLeg: state.tripInquiryFormikForm.legs.length === specialRequest.legIndex.length,
          };
        });
        return {
          ...state,
          tripInquiryFormikForm: {
            ...state.tripInquiryFormikForm,
            specialRequests,
          },
        };
      }
      return state;
    }
    case RESET_TRIP_INQUIRY_FORMIK_FORM: {
      return initialState;
    }
    default:
      return state;
  }
};

export default formikDataReducer;
